import { Box, Button } from '@material-ui/core';
import useCurrencyFormat from '@src/hooks/useCurrencyFormat';
import {
  addShoppingCart,
  onClickSendShopifyProduct,
  shopifyProductDetails,
  updateCarouselImage,
} from '@src/reducers/shopify';
import { isChatComposerDisabled } from '@src/selectors/agentConsole';
import {
  getSelectedLocationId,
  getShopifyCurrentStoreCanAddItemOutOfStock,
  getShopifyShoppingCartProductQuantity,
} from '@src/selectors/shopify';
import type { RootState } from '@src/store/reduxstore';
import { ShopifyProduct, ShopifyProductOrigin } from '@src/types/shopify';
import CarouselImage from '@src/utils/CarouselImage';
import { CSSProperties } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { SendIcon, addProductIcon } from './SvgIcons';

export interface IProduct {
  labels: { [key: string]: string };
  product: ShopifyProduct;
  inventoryQuantity: number;
  hasVariants: boolean;
  noVariants?: boolean;
  handleClickOnDetails: () => void;
  handleClickOnSend: () => void;
  handleUpdateCarouselImage: (imageUrl: string) => void;
  handleClickOnAddProduct: () => void;
  disableSendButtons: boolean;
  disableAddProductButton: boolean;
}

interface IcustomeButton {
  children?: React.ReactNode;
  style?: CSSProperties;
  [param: string]: unknown;
}
export const CustomButton: React.FC<IcustomeButton> = ({
  children,
  style,
  ...rest
}) => (
  <Button
    color="primary"
    variant="outlined"
    style={{ height: '28px', whiteSpace: 'nowrap', ...style }}
    {...rest}
  >
    {children}
  </Button>
);
interface IProductWrapper {
  product: ShopifyProduct;
}

const ProductWrapper: React.FC<IProductWrapper> = ({ product }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { formatMessage } = intl;
  const disableSendButtons = useSelector(isChatComposerDisabled);
  const locationId = useSelector(getSelectedLocationId);

  const labels = {
    brand: formatMessage({ id: 'Shopify.product.brand.label' }),
    details: formatMessage({ id: 'Shopify.product.details.button.label' }),
    price: formatMessage({ id: 'Shopify.product.price.label' }),
    priceAlt: formatMessage({ id: 'Shopify.product.price.alt.label' }),
    availability: formatMessage({
      id: 'Shopify.product.availability.label',
    }),
    noInventory: formatMessage({ id: 'Shopify.product.noInventory.label' }),
    units: formatMessage({
      id: 'Shopify.product.availability.units.label',
    }),
  };

  const { id, variants } = product;
  const hasVariants = variants.length > 1;
  const noVariants = variants.length === 0;
  const [defaultVariant] = variants;

  const shoppingCartProductQuantity = useSelector((state: RootState) =>
    getShopifyShoppingCartProductQuantity(state, `${defaultVariant?.id}`)
  );

  const canAddItemOutOfStock = useSelector(
    getShopifyCurrentStoreCanAddItemOutOfStock
  );

  const handleClickOnDetails = () => {
    dispatch(
      shopifyProductDetails({
        productId: id,
        locationId: Number(locationId),
      })
    );
  };

  const handleClickOnSend = () => {
    dispatch(
      onClickSendShopifyProduct({
        productId: id,
        locationId: Number(locationId),
        sendVariants: false,
      })
    );
  };

  const handleUpdateCarouselImage = (imageUrl: string) => {
    dispatch(updateCarouselImage({ productId: id, imageUrl }));
  };

  const handleClickOnAddProduct = () => {
    if (hasVariants) {
      handleClickOnDetails();
      return;
    }

    dispatch(
      addShoppingCart({
        variantId: `${defaultVariant?.id}`,
        quantity: 1,
        quantityTotal: defaultVariant?.inventoryQuantity,
        origin: ShopifyProductOrigin.LIST,
      })
    );
  };

  return (
    <Product
      product={product}
      labels={labels}
      inventoryQuantity={defaultVariant?.inventoryQuantity ?? 0}
      hasVariants={hasVariants}
      noVariants={noVariants}
      handleClickOnDetails={handleClickOnDetails}
      handleClickOnSend={handleClickOnSend}
      handleUpdateCarouselImage={handleUpdateCarouselImage}
      handleClickOnAddProduct={handleClickOnAddProduct}
      disableSendButtons={disableSendButtons}
      disableAddProductButton={
        shoppingCartProductQuantity >= defaultVariant?.inventoryQuantity &&
        !canAddItemOutOfStock
      }
    />
  );
};

export const Product: React.FC<IProduct> = ({
  product,
  labels,
  hasVariants,
  noVariants,
  inventoryQuantity,
  handleClickOnDetails,
  handleClickOnSend,
  handleUpdateCarouselImage,
  handleClickOnAddProduct,
  disableSendButtons,
  disableAddProductButton,
}) => {
  const { title, vendor, images, variants } = product;
  const { brand, details, price, priceAlt, availability, noInventory, units } =
    labels;

  const minPrice: { price: number; symbol: string } = variants.reduce(
    (curr, prev) => {
      if (Number(prev.price) < curr.price) {
        return {
          price: Number(prev.price),
          symbol: prev.currencySymbol,
        };
      }
      return { price: curr.price, symbol: curr.symbol };
    },
    { price: Number.MAX_SAFE_INTEGER, symbol: '' }
  );

  const formattedPrice = useCurrencyFormat({
    value: minPrice.price,
    symbol: minPrice.symbol,
  });
  const noPrice = minPrice.price === Number.MAX_SAFE_INTEGER;

  let priceLabel = price;
  if (hasVariants) priceLabel += ` ${priceAlt}`;
  priceLabel += ':';

  const sendIconStyle: CSSProperties =
    disableSendButtons || noVariants
      ? {
          cursor: 'default',
          pointerEvents: 'none',
          opacity: 0.5,
        }
      : { cursor: 'pointer' };

  return (
    <Box
      padding="8px 16px 8px 8px"
      color="text.primary"
      style={{
        boxShadow: '0px 8px 8px rgba(0, 0, 0, 0.08)',
      }}
    >
      <Box
        display="flex"
        justifyContent="center"
        width="100%"
        style={{
          boxShadow: '0px 8px 8px rgba(0, 0, 0, 0.08)',
          borderRadius: '8px',
        }}
      >
        <Box maxHeight="160px">
          <CarouselImage
            images={images.map(img => img.src)}
            height="160px"
            style={{
              boxShadow: '0px 8px 8px rgba(0, 0, 0, 0.08)',
              borderRadius: '8px',
              height: '160px',
            }}
            handleUpdateCarouselImage={handleUpdateCarouselImage}
          />
        </Box>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        paddingTop="8px"
        style={{
          gap: '8px',
        }}
      >
        <Box
          fontWeight={500}
          fontSize="14px"
          style={{ textTransform: 'capitalize' }}
        >
          {title}
        </Box>
        <Box fontWeight={400} fontSize={12}>
          {brand}: {vendor}
        </Box>
        {!hasVariants && (
          <Box fontSize={12}>
            <Box fontWeight={400} fontSize={12}>
              {availability}:{' '}
              {inventoryQuantity <= 0 ? (
                <Box fontWeight={600}>{noInventory}</Box>
              ) : (
                `${inventoryQuantity} ${units}`
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Box
        display="flex"
        justifyContent={noPrice ? 'end' : 'space-between'}
        alignItems="end"
        paddingTop="8px"
      >
        {!noPrice && (
          <Box fontSize={14} color="text.primary">
            <Box>{priceLabel}</Box>
            <Box fontWeight={500}>{formattedPrice}</Box>
          </Box>
        )}
        <Box
          onClick={handleClickOnSend}
          style={sendIconStyle}
          color="primary.main"
          data-testid="shopify-product-send-button"
        >
          {SendIcon}
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        paddingTop="15px"
        style={{ gap: 20 }}
      >
        <CustomButton
          onClick={handleClickOnDetails}
          style={{ flexGrow: 2 }}
          data-testid="shopify-product-details-button"
          disabled={noVariants}
        >
          {details}
        </CustomButton>

        {!hasVariants && (
          <CustomButton
            onClick={handleClickOnAddProduct}
            data-testid="shopify-product-add-button"
            disabled={
              disableSendButtons || disableAddProductButton || noVariants
            }
          >
            {addProductIcon}
          </CustomButton>
        )}
      </Box>
    </Box>
  );
};

export default ProductWrapper;
